import React from 'react';
import { TextField, InputAdornment } from '@material-ui/core';

/**
 * DateInput - A reusable date input component with proper validation
 * 
 * Features:
 * - Enforces 4-digit year limit (max: 9999-12-31)
 * - Sets reasonable minimum date (min: 1900-01-01)
 * - Handles null/undefined values gracefully
 * - Consistent styling across the application
 * - Proper Material-UI integration
 * 
 * @param {Object} props - Component props
 * @param {string} props.id - Input field ID
 * @param {string} props.label - Input field label
 * @param {string|number} props.value - Current value (YYYY-MM-DD format or null)
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.variant - Material-UI variant ('standard', 'outlined', 'filled')
 * @param {string} props.width - Custom width (default: '160px')
 * @param {Object} props.style - Additional styles
 * @param {string} props.key - React key for re-rendering
 * @param {boolean} props.fullWidth - Whether to take full width
 * @param {Object} props.InputProps - Additional InputProps for Material-UI
 */
const DateInput = ({
  id,
  label,
  value,
  onChange,
  required = false,
  variant = 'standard',
  width = '160px',
  style = {},
  key,
  fullWidth = false,
  InputProps = {},
  ...otherProps
}) => {
  // Handle null/undefined values gracefully
  const inputValue = value || '';

  // Default styles
  const defaultStyle = {
    marginTop: '5px',
    ...(fullWidth ? {} : { width: width }), // Only set width if not fullWidth
    ...style
  };

  // Default InputProps with date constraints
  const defaultInputProps = {
    startAdornment: (
      <InputAdornment position="start">
        <span />
      </InputAdornment>
    ),
    inputProps: {
      max: '9999-12-31',  // Enforce 4-digit year limit
      min: '1000-01-01'   // Set reasonable minimum date
    },
    ...InputProps
  };

  return (
    <TextField
      id={id}
      key={key}
      type="date"
      required={required}
      label={label}
      variant={variant}
      value={inputValue}
      fullWidth={fullWidth}
      style={defaultStyle}
      onChange={onChange}
      InputProps={defaultInputProps}
      {...otherProps}
    />
  );
};

export default DateInput;
